import { useCallback, useRef } from 'react';

import { FieldErrors } from 'react-hook-form';

type UseScrollToInvalidFieldProps = {
  behavior?: ScrollBehavior;
};

export const useScrollToInvalidField = <T extends Record<string, any>>({
  behavior = 'smooth',
}: UseScrollToInvalidFieldProps = {}) => {
  const fieldRefs = useRef<Record<string, HTMLElement | null>>({});

  const registerFieldRef = useCallback(
    (fieldName: string, ref: HTMLElement | null) => {
      fieldRefs.current[fieldName] = ref;
    },
    [],
  );

  const scrollToFirstInvalidField = useCallback(
    (errors: FieldErrors<T>) => {
      const elements = Object.keys(errors)
        .map(name => fieldRefs.current[name])
        .filter(el => !!el);
      elements.sort((a, b) => {
        const aRect = a?.getBoundingClientRect();
        const bRect = b?.getBoundingClientRect();
        if (!aRect || !bRect) return 0;
        return aRect.top - bRect.top;
      });

      if (elements.length > 0) {
        const errorElement = elements[0];
        errorElement?.scrollIntoView({ behavior, block: 'start' });
        errorElement?.focus();
      }
    },
    [behavior],
  );

  return {
    registerFieldRef,
    scrollToFirstInvalidField,
  };
};
